import {
  AiRejectedDocumentItem,
  IDDocumentType,
  VerificationType,
} from "@amzn/client-workbench-api-model";
import { Button, ButtonVariant } from "@amzn/stencil-react-components/button";
import { Col, Row } from "@amzn/stencil-react-components/layout";
import {
  MessageBanner,
  MessageBannerType,
} from "@amzn/stencil-react-components/message-banner";
import { Spinner } from "@amzn/stencil-react-components/spinner";
import { H4, Text } from "@amzn/stencil-react-components/text";
import IconCheckCircleFillLarge from "@amzn/stencil-react-icons/icons/icon-check-circle-fill-large";
import IconCloseCircleFillLarge from "@amzn/stencil-react-icons/icons/icon-close-circle-fill-large";
import React, { useContext, useState } from "react";

import idvQualityBlurryUpload from "@/assets/images/idvQualityBlurryUpload.png";
import idvQualityClearUpload from "@/assets/images/idvQualityClearUpload.png";
import idvQualityWebcamCover from "@/assets/images/idvQualityWebcamCover.png";
import idvQualityWindowsLaptop from "@/assets/images/idvQualityWindowsLaptop.png";
import FullIdVerification from "@/components/IdentityCheck/FullIdVerification";
import {
  DOCS_SUBMITTED_KEY,
  getAiRejectedDocuments,
  setAiRetryContext,
} from "@/components/IdentityCheck/helper/rivHelper";
import LightweightIdVerification from "@/components/IdentityCheck/LightweightIdVerification";
import { useUserProgress } from "@/hooks/useUserProgress";
import { AppContext } from "@/stores/appStore";
import { ActionType } from "@/stores/constants";
import { Day1EventCategory } from "@/types/snowplow-events";

/**
 * Surface the AI quality gate's per-artifact rejection details
 * and let the new hire request fresh presigned URLs for the flagged
 * artifacts only. After the user uploads the retry, the AI gate is bypassed
 * and the bundle proceeds straight to OCR + the human verifier (regardless
 * of the retry's quality), so the wording here emphasises that this is a
 * one-shot opportunity to improve the submission.
 */
export default function AiQualityRejected(): JSX.Element {
  const { dispatch } = useContext(AppContext);
  const { data: userProgress } = useUserProgress();
  const [retryReady, setRetryReady] = useState(false);

  const aiRejectedDocs: AiRejectedDocumentItem[] = userProgress
    ? getAiRejectedDocuments(userProgress.verificationStatus)
    : [];

  const verificationType: VerificationType = (() => {
    // Derive from the progress data rather than docsVerificationStatus, which
    // is AI_REJECTED at this point and doesn't indicate light vs full.
    if (userProgress) {
      const lightProgress = userProgress.verificationStatus.find(
        (p) => p.VerificationType === "ON_BOARDING_LIGHT_VERIFICATION"
      );
      const fullProgress = userProgress.verificationStatus.find(
        (p) => p.VerificationType === "ON_BOARDING_FULL_VERIFICATION"
      );
      const isLight =
        lightProgress &&
        lightProgress.VerificationTypeStatus !== "NOT_APPLICABLE" &&
        fullProgress?.VerificationTypeStatus === "NOT_APPLICABLE";
      if (isLight) {
        return "ON_BOARDING_LIGHT_VERIFICATION";
      }
    }
    return "ON_BOARDING_FULL_VERIFICATION";
  })();

  const handleRetry = (): void => {
    if (aiRejectedDocs.length === 0) {
      return;
    }
    const docTypes: IDDocumentType[] = aiRejectedDocs.map(
      (item) => item.documentType
    );

    setAiRetryContext({ verificationType, documentTypes: docTypes });

    localStorage.removeItem(DOCS_SUBMITTED_KEY);

    dispatch({
      type: ActionType.REMOVE_DOCUMENTS_BY_TYPES,
      removeDocumentTypes: docTypes as string[],
    });

    setRetryReady(true);
  };

  if (!userProgress) {
    return <Spinner />;
  }

  // Once retry presigned URLs are cached, render the recording component
  // directly. We bypass the IdentityCheck switch because isManualIdvInFlight
  // would route to VerificationPending instead of the recording UI.
  if (retryReady) {
    if (verificationType === "ON_BOARDING_LIGHT_VERIFICATION") {
      return (
        <LightweightIdVerification
          eventCategory={Day1EventCategory.IDCheckLight}
        />
      );
    }
    return <FullIdVerification allowAutomatedIdvFallback={false} />;
  }

  // Ensure primary ID documents always appear before secondary.
  const sortedDocs = [...aiRejectedDocs].sort((a, b) => {
    const priority = (docType: IDDocumentType): number => {
      switch (docType) {
        case "PRIMARY_ID":
        case "PRIMARY_ID_RECORDING":
          return 0;
        case "SECONDARY_ID":
        case "SECONDARY_ID_RECORDING":
          return 1;
        default:
          return 2;
      }
    };
    return priority(a.documentType) - priority(b.documentType);
  });

  return (
    <Col gridGap="S400" dataTestId="ai-quality-rejected">
      <H4>We need you to retake one or more uploads</H4>
      <Col gridGap="S400">
        {sortedDocs.map((doc) => (
          <Col
            key={doc.documentType}
            gridGap="S200"
            dataTestId={`ai-quality-rejected-item-${doc.documentType}`}
          >
            <Text fontWeight="bold">
              {humanizeDocumentType(doc.documentType)}:
            </Text>
            <MessageBanner type={MessageBannerType.Error} isDismissible={true}>
              <Text>{summarizeFailureReasons(doc.failureReasons)}</Text>
            </MessageBanner>
            {renderExampleImages(doc.failureReasons)}
          </Col>
        ))}
      </Col>
      <Row gridGap="S300">
        <Button
          variant={ButtonVariant.Primary}
          onClick={(): void => {
            handleRetry();
          }}
          disabled={aiRejectedDocs.length === 0}
          dataTestId="ai-quality-retry-button"
        >
          Re-record now
        </Button>
      </Row>
    </Col>
  );
}

/**
 * Render do/don't example images below the error banner when the failure
 * reasons match a category that has illustrative guidance.
 */
function renderExampleImages(reasons: string[]): JSX.Element | null {
  if (reasons.includes("LOW_QUALITY_CAMERA_BLOCKED")) {
    return (
      <Row
        gridGap="S400"
        padding="S400"
        backgroundColor="color.surface.bg-muted"
        justifyContent="center"
        alignItems="flex-start"
        dataTestId="ai-quality-example-camera-blocked"
      >
        <Col alignItems="center" gridGap="S200">
          <IconCheckCircleFillLarge
            color="color.status.success-icon"
            aria-hidden
          />
          <img
            src={idvQualityWindowsLaptop}
            alt="Laptop with webcam uncovered"
            width={244}
            height={174}
          />
        </Col>
        <Col alignItems="center" gridGap="S200">
          <IconCloseCircleFillLarge
            color="color.status.critical-icon"
            aria-hidden
          />
          <img
            src={idvQualityWebcamCover}
            alt="Laptop with webcam covered"
            width={244}
            height={174}
          />
        </Col>
      </Row>
    );
  }

  // Return null for higher-priority reasons that lack illustrative guidance,
  // so we don't show misleading blurry/clear images when the text message
  // is about a different issue (e.g. "show the front of your ID").
  if (
    reasons.includes("LOW_QUALITY_BLANK") ||
    reasons.includes("LOW_QUALITY_WRONG_SIDE")
  ) {
    return null;
  }

  if (
    reasons.includes("LOW_QUALITY_BLURRY") ||
    reasons.includes("LOW_QUALITY_OUT_OF_FOCUS") ||
    reasons.includes("LOW_QUALITY_GLARE") ||
    reasons.includes("LOW_QUALITY_OBSCURED")
  ) {
    return (
      <Row
        gridGap="S400"
        padding="S400"
        backgroundColor="color.surface.bg-muted"
        justifyContent="center"
        alignItems="flex-start"
        dataTestId="ai-quality-example-blurry"
      >
        <Col alignItems="center" gridGap="S200">
          <IconCheckCircleFillLarge
            color="color.status.success-icon"
            aria-hidden
          />
          <img
            src={idvQualityClearUpload}
            alt="Clear ID document — readable text and visible details"
            width={244}
            height={174}
          />
        </Col>
        <Col alignItems="center" gridGap="S200">
          <IconCloseCircleFillLarge
            color="color.status.critical-icon"
            aria-hidden
          />
          <img
            src={idvQualityBlurryUpload}
            alt="Blurry ID document — text is not legible"
            width={244}
            height={174}
          />
        </Col>
      </Row>
    );
  }

  return null;
}

// TODO(NHE-6128): Migrate all user-facing strings to i18n keys via <FormattedMessage />
// before enabling this feature for non-English countries.
// See: https://taskei.amazon.dev/tasks/NHE-6128

/**
 * Map an ``IDDocumentType`` to human-readable text for the rejection list.
 */
function humanizeDocumentType(documentType: IDDocumentType): string {
  switch (documentType) {
    case "PRIMARY_ID":
      return "Primary ID photo";
    case "PRIMARY_ID_RECORDING":
      return "Primary ID recording";
    case "SECONDARY_ID":
      return "Secondary ID photo";
    case "SECONDARY_ID_RECORDING":
      return "Secondary ID recording";
    case "LIVENESS_RECORDING":
      return "Gestures recording";
    default:
      return documentType;
  }
}

/**
 * Summarise the AI quality agent's failure reasons in user-facing text.
 */
function summarizeFailureReasons(reasons: string[]): string {
  if (reasons.length === 0) {
    return "We couldn't read your ID. Please try again and follow the instructions on your next attempt to make sure your ID is readable.";
  }

  // Priority-ordered: return the first matching message. Each condition
  // maps to a distinct Figma screen so we show one clear instruction
  // rather than combining multiple potentially confusing snippets.
  if (reasons.includes("LOW_QUALITY_CAMERA_BLOCKED")) {
    return "It looks like something is blocking your camera. If your device has a physical camera cover, make sure it's open.";
  }
  if (reasons.includes("LOW_QUALITY_BLANK")) {
    return "We weren't able to detect an ID document in your video. Please hold your ID in front of the camera and make sure it's clear and fully visible.";
  }
  if (reasons.includes("LOW_QUALITY_WRONG_SIDE")) {
    return "Please show the front of your ID. Make sure your photo and details are visible to the camera.";
  }
  if (
    reasons.includes("LOW_QUALITY_GLARE") ||
    reasons.includes("LOW_QUALITY_OBSCURED")
  ) {
    return "We couldn't read the details on your ID. Make sure nothing is covering it and there's no glare, then try again.";
  }

  // Generic fallback for other quality issues (blurry, out of focus,
  // over/under exposed, filter applied, etc.)
  return "We couldn't read your ID. Please try again and follow the instructions on your next attempt to make sure your ID is readable.";
}
